export type SystemLogLevel = 'error' | 'warn' | 'info' | 'debug';

export type SystemLogSystem = 'portal' | 'app';

export interface SystemLog {
  id: number;
  occurred_at: string;
  log_level: SystemLogLevel;
  log_system: SystemLogSystem;
  message: string;
  payload: Record<string, unknown> | null;
  function_name: string | null;
  operation: string | null;
  request_id: string | null;
  user_id: string | null;
  http_status: number | null;
}

export function getSystemLogSummary(log: SystemLog): string {
  const trimmed = log.message.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as { errorMessage?: unknown; operation?: unknown; functionName?: unknown };
      if (typeof parsed.errorMessage === 'string' && parsed.errorMessage.trim()) {
        return parsed.errorMessage.trim();
      }
      if (typeof parsed.operation === 'string' && parsed.operation.trim()) {
        return parsed.operation.trim();
      }
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}

const SENT_PAYLOAD_EXCLUDED_KEYS = new Set(['errorName', 'errorMessage', 'stack', 'response', 'status']);

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readPayloadString(payload: Record<string, unknown> | null, key: string): string {
  const value = payload?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function formatPayloadValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function getSystemLogError(log: SystemLog): string {
  const errorMessage = readPayloadString(log.payload, 'errorMessage');
  if (errorMessage) {
    return errorMessage;
  }

  return getSystemLogSummary(log);
}

export function getSystemLogErrorDetails(log: SystemLog): string {
  const errorName = readPayloadString(log.payload, 'errorName');
  const errorMessage = getSystemLogError(log);
  const stack = readPayloadString(log.payload, 'stack');
  const response = formatPayloadValue(log.payload?.response);
  return [errorName, errorMessage, stack, response].filter(Boolean).join('\n\n');
}

export function getSystemLogSentPayload(log: SystemLog): Record<string, unknown> | null {
  if (!log.payload) {
    return null;
  }

  if (isJsonRecord(log.payload.payload)) {
    return log.payload.payload;
  }

  const sent = Object.fromEntries(
    Object.entries(log.payload).filter(([key]) => !SENT_PAYLOAD_EXCLUDED_KEYS.has(key)),
  );

  return Object.keys(sent).length > 0 ? sent : null;
}

export interface SystemLogFilters {
  search?: string;
  logLevel?: SystemLogLevel;
  logSystem?: SystemLogSystem;
  dateFrom?: string;
  dateTo?: string;
}

export function formatSystemLogDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDefaultSystemLogDateRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const dateTo = formatSystemLogDateInputValue(today);
  const fromDate = new Date(today);
  fromDate.setMonth(fromDate.getMonth() - 3);
  return {
    dateFrom: formatSystemLogDateInputValue(fromDate),
    dateTo,
  };
}

export const SYSTEM_LOG_LEVEL_LABELS: Record<SystemLogLevel, string> = {
  error: 'Erro',
  warn: 'Aviso',
  info: 'Informação',
  debug: 'Debug',
};

export const SYSTEM_LOG_SYSTEM_LABELS: Record<SystemLogSystem, string> = {
  portal: 'Portal',
  app: 'App',
};

export const SYSTEM_LOG_LEVEL_OPTIONS: Array<{ value: SystemLogLevel; label: string }> = [
  { value: 'error', label: SYSTEM_LOG_LEVEL_LABELS.error },
  { value: 'warn', label: SYSTEM_LOG_LEVEL_LABELS.warn },
  { value: 'info', label: SYSTEM_LOG_LEVEL_LABELS.info },
  { value: 'debug', label: SYSTEM_LOG_LEVEL_LABELS.debug },
];

export const SYSTEM_LOG_SYSTEM_OPTIONS: Array<{ value: SystemLogSystem; label: string }> = [
  { value: 'portal', label: SYSTEM_LOG_SYSTEM_LABELS.portal },
  { value: 'app', label: SYSTEM_LOG_SYSTEM_LABELS.app },
];